// Per subscribe type: the field holding the portfolio item's ID, and the field
// holding the ID of the project's own copy of it.
const fieldsByType = {
    MSIEffect: { source: 'Effect', project: 'ProjectEffect' },
    Effect: { source: 'Effect', project: 'ProjectEffect' },
    Activity: { source: 'Activity', project: 'ProjectActivity' },
    Targetgroup: { source: 'Targetgroup', project: 'ProjectTargetgroup' },
};

const isDeletedStatus = (status) => status === 'deleted' || status === 'deleted-project';

// A link counts until either side ends it (Status) or it is soft-deleted (ADR-008).
const isActiveSync = (row) => !!row && !row.Deleted && !isDeletedStatus(row.Status);

/**
 * Whether a Synchronisations row is an active link of one of `types` to `parentId`.
 *
 * @param {object} row - A Synchronisations row
 * @param {string} parentId - CompagnyID of the portfolio
 * @param {Array<string>} types - Sync types to look at
 * @returns {boolean}
 */
const isActiveLinkOfType = (row, parentId, types) =>
    row?.Parent === parentId && types.includes(row.Type) && isActiveSync(row);

/**
 * Portfolio-side IDs that the current project already has a live copy of.
 * The subscribe modals lock these, so picking them again can't copy an effect
 * (and its questions) a second time — inbox #291.
 *
 * A link only counts while the project's own copy still exists: deleting an
 * activity or targetgroup leaves its Synchronisations row 'paired', and that
 * row alone would lock the item forever.
 *
 * @param {Array<object> | '' | undefined | null} syncRows - The project's Synchronisations rows ('' while loading)
 * @param {string} parentId - CompagnyID of the portfolio the modal shows
 * @param {Array<string>} types - Sync types to look at, e.g. ['MSIEffect', 'Effect']
 * @param {Array<{ID: string, Deleted?: boolean}> | '' | undefined | null} localItems - The project's own items of that kind ('' while loading)
 * @returns {Set<string>} IDs of the portfolio items that are already linked
 */
const linkedSourceIds = (syncRows, parentId, types, localItems) => {
    if (!Array.isArray(syncRows) || !Array.isArray(localItems)) return new Set();

    const liveLocalIds = new Set(localItems.filter((item) => item && !item.Deleted).map((item) => item.ID));

    return new Set(
        syncRows
            .filter((row) => isActiveLinkOfType(row, parentId, types))
            .filter((row) => liveLocalIds.has(row[fieldsByType[row.Type].project]))
            .map((row) => row[fieldsByType[row.Type].source])
            .filter(Boolean)
    );
};

export default linkedSourceIds;
export { fieldsByType, isActiveLinkOfType };
